import { pathOr } from 'ramda'
import { createSelector } from 'reselect'

import { RootState } from 'redux/types'

import { CartState, CartItem } from '../helpers/types'

const getCartState = (state: RootState): CartState => state.cart
const getOrderTotals = (state): CartState['orderTotals'] => state.orderTotals
const getIsCartLoading = (state): CartState['isCartLoading'] => getCartState(state).isCartLoading
const getIsCartPopulated = (state): CartState['isCartPopulated'] =>
  getCartState(state).isCartPopulated

const getCartItems = (state): CartState['items'] => getCartState(state).items || []
const getOOSItems = (state): CartState['oosItems'] => getCartState(state).oosItems || []
const getHazmatItems = (state): CartState['items'] => getCartState(state).hazmatItems || []
const getAllCartItems = (state): CartState['items'] => [
  ...getCartItems(state),
  ...getOOSItems(state),
  ...getHazmatItems(state),
]
const getHiddenItems = (state): CartState['hiddenItems'] => getCartState(state).hiddenItems || []
const getBrands = (state): CartState['brands'] => getCartState(state).brands || []
const getHasMultiParcel = createSelector(getBrands, (brands) => {
  return brands.length > 1
})
const getPaymentModes = (state): CartState['paymentModes'] => getCartState(state).paymentModes || []
const getSupportedCreditCards = (state): CartState['supportedCreditCards'] =>
  getCartState(state).supportedCreditCards || []

const getHasItemWithClubPrice = createSelector(
  getCartItems,
  getOOSItems,
  getHazmatItems,
  (cartItems, oosItems, hazmatItems) => {
    const items = [...cartItems, ...oosItems, ...hazmatItems]

    return !!items.filter((item) => item.isYellowPriceAvailable).length
  },
)

const getDeliveryAddress = (state): CartState['deliveryAddress'] =>
  getCartState(state).deliveryAddress
const getPickUpInStoreData = (state): CartState['pickUpInStoreData'] =>
  getCartState(state).pickUpInStoreData

const getCodLimitReached = (state): CartState['codLimitReached'] =>
  getCartState(state).codLimitReached
const getIsVatMessageRequired = (state): CartState['vatMessageRequired'] =>
  getCartState(state).vatMessageRequired
const getIsTaxDisclaimerRequired = (state): CartState['taxDisclaimerRequired'] =>
  getCartState(state).taxDisclaimerRequired
const getIsReturnAndRefundRequired = (state): CartState['returnAndRefundRequired'] =>
  getCartState(state).returnAndRefundRequired
const getIsFineprintReturnAndRefundRequired = (
  state,
): CartState['fineprintReturnAndRefundRequired'] =>
  getCartState(state).fineprintReturnAndRefundRequired
const getReturnDays = (state): CartState['returnDays'] => getCartState(state).returnDays

const getOneClickCheckoutInfo = (state): CartState['oneClickCheckoutInfo'] =>
  getCartState(state).oneClickCheckoutInfo || {}
const getOneClickCheckoutPaymentData = (state): CartState['oneClickCheckoutPaymentData'] =>
  getCartState(state).oneClickCheckoutPaymentData || {}
const getOneClickCheckoutLoading = (state): CartState['oneClickCheckoutLoading'] =>
  getCartState(state).oneClickCheckoutLoading

const getModalError = (state): CartState['modalError'] => getCartState(state).modalError
const getInfoMessage = (state): CartState['infoMessage'] => getCartState(state).infoMessage

const getCartItem = (variantCode, state): CartItem | undefined =>
  getAllCartItems(state).find((item) => item.variantCode === variantCode)

const getCartCode = (state): CartState['cartCode'] => getCartState(state).cartCode || ''
const getCartOwner = (state): CartState['cartOwner'] => getCartState(state).cartOwner || ''
const getCartSubTotal = (state): number => pathOr(0, ['subTotal', 'value'], getOrderTotals(state))

const getAppliedGiftCards = (state): CartState['giftCardsApplied'] =>
  getCartState(state).giftCardsApplied || []
const addGiftCardStatus = (state): CartState['addGiftCardStatus'] =>
  getCartState(state).addGiftCardStatus
const getGiftCardError = (state): CartState['giftCardError'] => getCartState(state).giftCardError

const getIsMiddlePage = (state): CartState['isMiddlePage'] => getCartState(state).isMiddlePage

export default {
  getIsCartLoading,
  getIsCartPopulated,

  getCartItems,
  getOOSItems,
  getHazmatItems,
  getHiddenItems,
  getBrands,
  getHasMultiParcel,
  getPaymentModes,
  getSupportedCreditCards,
  getIsMiddlePage,

  getHasItemWithClubPrice,

  getDeliveryAddress,
  getPickUpInStoreData,

  getCodLimitReached,
  getIsVatMessageRequired,
  getIsTaxDisclaimerRequired,
  getIsReturnAndRefundRequired,
  getIsFineprintReturnAndRefundRequired,
  getReturnDays,

  getOneClickCheckoutInfo,
  getOneClickCheckoutPaymentData,
  getOneClickCheckoutLoading,

  getModalError,
  getInfoMessage,

  getCartItem,

  getCartCode,
  getCartOwner,
  getCartSubTotal,
  getOrderTotals,

  getAppliedGiftCards,
  addGiftCardStatus,
  getGiftCardError,
}
